import { useDidUpdate, usePrevious } from '@mantine/hooks';
import { useEffect, useMemo, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { usePortfolioProjects } from '../../api/summary-client/summary-client.hooks';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../store/process-analysis-store/process-analysis-store';
import {
  pruneSelectionsToScope,
  resetFilters,
  setActivePanel,
  setSelectedDataPoint,
} from '../../store/process-analysis-store/process-analysis-store.actions';
import { useComparisonMode, usePrimaryEntity } from '../../store/process-analysis-store/process-analysis-store.hooks';
import { Panel, type SelectionScope } from '../../store/process-analysis-store/process-analysis-store.type';
import { secondaryBase } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import type { ExtendedComboboxItem } from '../../ui-library/select/select.type';
import { useAvailableMeasuresData } from './hooks/available-measures';
import { useInitiativeCompletionData, useInitiativeFocusData, useInitiativesData } from './hooks/initiative-data';
import { getAvailableBoardIds, getAvailableTeamIds } from './process-analysis.helpers';
import { ComparisonMode, MultipleViewMode, Tab, ViewMode } from './process-analysis.type';

/**
 * Main hook that fetches process analysis data
 *
 * @returns {Object} Object containing loading state
 */
const useProcessAnalysisData = () => {
  const { isLoading: isAvailableMeasuresLoading } = useAvailableMeasuresData();

  useInitiativesData();
  useInitiativeCompletionData();
  useInitiativeFocusData();

  return {
    isLoading: isAvailableMeasuresLoading,
  };
};

/**
 * Hook that returns view mode options for the process analysis view selector
 *
 * @returns {ExtendedComboboxItem[]} Array of view mode options with labels, values and icons
 * Each option contains:
 * - label: Translated text for the view mode
 * - value: ViewMode enum value
 * - leftSection: Icon component for the view mode
 * For Portfolios tab, includes an additional Capacity view mode option
 */
const useSingleViewModeOptions = (): ExtendedComboboxItem[] => {
  const i18n = useTranslation();
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const comparisonMode = useComparisonMode();

  const options = useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      const baseOptions: ExtendedComboboxItem[] = [
        {
          label: i18n.t('process_analysis.view_mode.measure_comparison'),
          value: ViewMode.MeasureComparison,
          leftSection: <Icon name="compress" size={20} color={secondaryBase} />,
        },
        {
          label: i18n.t('process_analysis.view_mode.initiative_overview'),
          value: ViewMode.InitiativeOverview,
          leftSection: <Icon name="shape_line" size={20} color={secondaryBase} />,
        },
        {
          label: i18n.t('process_analysis.view_mode.initiative_completion'),
          value: ViewMode.InitiativeCompletion,
          leftSection: <Icon name="compare_arrows" size={20} color={secondaryBase} />,
        },
        {
          label: i18n.t('process_analysis.view_mode.initiative_focus'),
          value: ViewMode.InitiativeFocus,
          leftSection: <Icon name="center_focus_weak" size={20} color={secondaryBase} />,
        },
      ];

      if (activeTab === Tab.Portfolios) {
        const capacityOption: ExtendedComboboxItem = {
          label: i18n.t('process_analysis.view_mode.capacity'),
          value: ViewMode.Capacity,
          leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
        };

        return [baseOptions[0], capacityOption, ...baseOptions.slice(1)];
      }

      return baseOptions;
    }

    return [];
  }, [activeTab, comparisonMode, i18n.t]);

  return options;
};

/**
 * Hook that returns an array of view mode options for multiple comparison mode
 *
 * Returns base options of Monthly Averages and Key Measures Over Time views.
 * For Teams tab, includes an additional Capacity view mode option.
 *
 * @returns {ExtendedComboboxItem[]} Array of view mode options with labels, values and icons
 */
const useMultipleViewModeOptions = (): ExtendedComboboxItem[] => {
  const i18n = useTranslation();
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const comparisonMode = useComparisonMode();

  const options = useMemo(() => {
    if (comparisonMode === ComparisonMode.Multiple) {
      const baseOptions: ExtendedComboboxItem[] = [
        {
          label: i18n.t('process_analysis.view_mode.monthly_averages'),
          value: MultipleViewMode.MonthlyAverages,
          leftSection: <Icon name="stacked_line_chart" size={20} color={secondaryBase} />,
        },
        {
          label: i18n.t('process_analysis.view_mode.key_measures_over_time'),
          value: MultipleViewMode.KeyMeasuresOverTime,
          leftSection: <Icon name="area_chart" size={20} color={secondaryBase} />,
        },
      ];

      if (activeTab === Tab.Teams) {
        const capacityOption: ExtendedComboboxItem = {
          label: i18n.t('process_analysis.view_mode.capacity'),
          value: ViewMode.Capacity,
          leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
        };

        return [...baseOptions, capacityOption];
      }

      return baseOptions;
    }

    return [];
  }, [activeTab, comparisonMode, i18n.t]);

  return options;
};

/**
 * Hook that resets the side section when the primary entity or comparison mode changes
 *
 * @returns {void}
 */
const useSideSectionReset = (): void => {
  const primaryEntity = usePrimaryEntity();
  const comparisonMode = useComparisonMode();

  useDidUpdate(() => {
    if (primaryEntity || comparisonMode === ComparisonMode.Multiple) {
      setSelectedDataPoint(null);
      setActivePanel(Panel.Filters);
    }
  }, [primaryEntity, comparisonMode]);
};

/**
 * Hook that provides a scroll area ref and automatically scrolls to top when viewMode changes
 *
 * @returns {RefObject<HTMLDivElement | null>} scrollAreaRef - Ref to be used with ScrollArea's viewportRef prop
 */
const useScrollToTop = (): RefObject<HTMLDivElement | null> => {
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const viewMode = useProcessAnalysisStore((state) => state.viewMode);
  const multipleViewMode = useProcessAnalysisStore((state) => state.multipleViewMode);
  const comparisonMode = useComparisonMode();

  useDidUpdate(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({ top: 0 });
    }
  }, [viewMode, multipleViewMode, comparisonMode]);

  return scrollAreaRef;
};

/**
 * Hook that resets filters when portfolio changes in multiple comparison mode
 *
 * @returns {void}
 */
const useFiltersReset = (): void => {
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const comparisonMode = useComparisonMode();

  const previousPortfolioId = usePrevious(portfolioId);

  useDidUpdate(() => {
    const shouldResetFilters =
      previousPortfolioId && portfolioId !== previousPortfolioId && comparisonMode === ComparisonMode.Multiple;

    if (shouldResetFilters) {
      resetFilters();
    }
  }, [portfolioId, comparisonMode, previousPortfolioId]);
};

/**
 * Hook that drops entity selections left over from a previously active scope.
 *
 * Responsibilities:
 * 1. Resolve which portfolios are still comparable in the active organization
 * 2. Resolve which projects/boards still exist in the active portfolio
 * 3. Hand both to `pruneSelectionsToScope`, which drops the ids that no longer
 *    resolve and leaves the rest — and any scope still unknown — alone
 *
 * Without this, a portfolio switch leaves the persisted selection pointing at the
 * previous portfolio: the project selector shows "1 Project" while none of its
 * options are checked, and the page keeps querying a project outside the active
 * scope. `useFiltersReset` covers only Multiple comparison mode, which a single
 * selection never reaches.
 *
 * An empty list is never mistaken for an unresolved one. `portfolios` is written
 * in a single atomic update by `initGlobalState`, so `[]` there means "not loaded"
 * and defers the prune; once loaded, an empty comparable set legitimately clears
 * the selection. For projects/boards the same distinction comes from the query
 * cache: `undefined` defers, `[]` is a portfolio that genuinely has no projects.
 * With no active portfolio at all, no request will ever land to resolve the scope,
 * so it is treated as known-empty rather than deferring forever.
 *
 * The project list is read under the *current* portfolio id, matching
 * `useAdoptionTeamFilter`: it shares the app-level initializer's query key, so it
 * reads that cache entry rather than issuing a second request, while staying
 * enabled so the scope still resolves if the initializer never ran. The global
 * store's `teams` cannot be used instead — it still holds the previous portfolio's
 * projects while the new portfolio's request is in flight, and pruning against
 * that list would be a no-op that lets the stale selection survive.
 *
 * @returns {void}
 */
const useScopedSelectionReset = (): void => {
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolios = useGlobalStore((state) => state.portfolios);

  // Re-run on every selection change, not just on scope changes: the URL sync in
  // `ProcessAnalysis` is one-shot and writes ids after mount, and on a warm
  // navigation no scope dependency changes afterwards to trigger a re-check.
  // Pruning is idempotent, so re-running it costs a comparison and nothing else.
  const selectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);

  const { projects: scopedTeams } = usePortfolioProjects(portfolioId, {
    enabled: !!portfolioId,
    staleTime: 1000 * 60 * 5,
  });

  // Mirrors `usePortfolioOptions`: only portfolios in the active organization are
  // comparable, and the active portfolio itself is never one of the comparands.
  const availablePortfolioIds = useMemo(() => {
    if (!organizationId || portfolios.length === 0) {
      return undefined;
    }

    return new Set(
      portfolios
        .filter((portfolio) => portfolio.organization.id === organizationId && portfolio.id !== portfolioId)
        .map(({ id }) => id),
    );
  }, [portfolios, organizationId, portfolioId]);

  const availableProjectScope = useMemo(() => {
    if (!portfolioId) {
      return { teamIds: new Set<string>(), boardIds: new Set<string>() };
    }

    if (!scopedTeams) {
      return undefined;
    }

    return { teamIds: getAvailableTeamIds(scopedTeams), boardIds: getAvailableBoardIds(scopedTeams) };
  }, [portfolioId, scopedTeams]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the selections are re-run triggers, not values the effect reads — it prunes whatever the store holds at that moment
  useEffect(() => {
    const scope: SelectionScope = { portfolioIds: availablePortfolioIds, ...availableProjectScope };

    pruneSelectionsToScope(scope);
  }, [availablePortfolioIds, availableProjectScope, selectedPortfolios, selectedTeams, selectedBoards]);
};

export {
  useFiltersReset,
  useMultipleViewModeOptions,
  useProcessAnalysisData,
  useScopedSelectionReset,
  useScrollToTop,
  useSideSectionReset,
  useSingleViewModeOptions,
};
